import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  ViewContainerRef,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { HttpErrorResponse } from '@angular/common/http';
import { Subject, takeUntil, finalize, filter, mergeMap, switchMap, Observable, shareReplay, catchError, EMPTY } from 'rxjs';
import moment from 'moment';

import { IdentityReportsService } from '@app/core/services/identity-reports.service';
import { NotificationService } from '@app/core/services/notification.service';
import { Client } from '@app/core/types/client';
import { BrandCodes } from '@app/core/types/brand';
import {
  ID_VERIFICATION_STATE_ICON,
  ID_VERIFICATION_STATE_CLASS,
  PROCESSOR_COLOR,
  IdentityVerification,
  IdentityVerificationEvent,
  ID_VERIFICATION_STATE,
  VERIFICATION_FLOW,
  StartVerificationDialogData,
  FLOW_OPTIONS,
  VerificationDecision,
  FlowOption,
  eventType,
} from '@app/core/types/identity-verification';
import { IdVerificationSocket } from '@app/main/socket-io/id-verification-socket.service';
import { StartVerificationDialogComponent } from '../start-identity-verification-dialog/start-identity-verification-dialog.component';
import { TranslateService } from '@ngx-translate/core';
import { DialogProviderService } from '@app/core/services/dialog-provider.service';
import { VerificationVideoModalComponent } from '../verification-video-modal-component/verification-video-modal.component';
import { ShuftiRequestService } from '@app/core/services/shufti-request.service';

interface VerificationListItem {
  id: number;
  state: ID_VERIFICATION_STATE;
  startedAt: string;
  stateClass: string;
  flow: FlowOption;
}

interface EventView {
  id: number;
  type: string;
  typeDisplay: string;
  processor: string;
  createdAt: string;
  data: Record<string, unknown> | null;
  dotColor: string;
  isFail: boolean;
  isSuccess: boolean;
  procColor: string;
  procColorBg: string;
  dataEntries: [string, unknown][];
  hasData: boolean;
}

interface VerificationDetailView {
  id: number;
  state: ID_VERIFICATION_STATE;
  stateClass: string;
  flow: VERIFICATION_FLOW;
  startedAt: string;
  endedAt: string | null;
  duration: string;
  miReportId: number | null;
  events: EventView[];
}

@Component({
  selector: 'itfg-identity-verification',
  templateUrl: './identity-verification.component.html',
  styleUrl: './identity-verification.component.scss',
  standalone: false,
})
export class IdentityVerificationComponent
  implements OnInit, OnChanges, OnDestroy {
  @Input() client: Client;

  public STATE_ICON = ID_VERIFICATION_STATE_ICON;
  public STATE_CLASS = ID_VERIFICATION_STATE_CLASS;
  public PROC_COLOR = PROCESSOR_COLOR;
  public FLOW_OPTIONS = FLOW_OPTIONS;
  public verficationDecisions: typeof VerificationDecision =
    VerificationDecision;

  public verifications: IdentityVerification[] = [];
  public verificationItems: VerificationListItem[] = [];
  public currentIndex: number = 0;
  public currentDetail: IdentityVerification | null = null;
  public detailView: VerificationDetailView | null = null;

  public loadingList: boolean = false;
  public loadingDetail: boolean = false;
  public expanded: boolean = false;
  public verificationStartedFromHere: boolean = false;
  public selectedEventId: string | number | null = null;
  public newVerificationIds: Set<number> = new Set<number>();

  public unsubscribe = new Subject<void>();

  private videoBlob$?: Observable<Blob>;
  public videoReady: boolean | null = null;

  constructor(
    public service: IdentityReportsService,
    public notificationService: NotificationService,
    public idVerificationSocket: IdVerificationSocket,
    public dialog: MatDialog,
    public translate: TranslateService,
    public dialogProvider: DialogProviderService,
    public viewContainerRef: ViewContainerRef,
    private shuftiRequestService: ShuftiRequestService
  ) { }

  ngOnInit() {
    this.idVerificationSocket
      .onVerificationUpdated()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((verification: IdentityVerification) => {
        this.currentDetail = verification;
        this.detailView = this.buildDetailView(verification);
        this.service.identityVerificationDetail$.next(verification);
        this.loadList();
      });

    this.idVerificationSocket
      .onVerificationAdded()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((verification: IdentityVerification) => {
        if (!this.verificationStartedFromHere) {
          this.verifications = [verification, ...this.verifications];
          this.verificationItems = this.verifications.map((v) =>
            this.buildListItem(v)
          );
          this.currentIndex += 1;
          this.newVerificationIds.add(verification.id);
        }
      });
  }

  ngOnChanges(): void {
    if (this.client?.id && this.client?.civilId) {
      this.service.identityVerificationDetail$.next(undefined);
      this.idVerificationSocket.joinClientRoom(
        this.client.civilId,
        this.client.brand.code
      );
      this.loadList();
    }
  }

  get activeVerificationsCount(): number {
    return this.verifications.filter(
      (v) =>
        v.state === ID_VERIFICATION_STATE.IN_PROGRESS ||
        v.state === ID_VERIFICATION_STATE.IN_REVIEW ||
        v.state === ID_VERIFICATION_STATE.INITIALIZED
    ).length;
  }

  get currentVerificationIsActive(): boolean {
    const s = this.currentDetail?.state;
    return (
      s === ID_VERIFICATION_STATE.IN_PROGRESS ||
      s === ID_VERIFICATION_STATE.IN_REVIEW
    );
  }

  get enableShowVideoInMatMenu(): boolean {
    return (
      !!this.currentDetail &&
      this.currentDetail?.state !== ID_VERIFICATION_STATE.INITIALIZED &&
      this.currentDetail?.flow === VERIFICATION_FLOW.SHUFTI &&
      this.videoReady
    );
  }

  flowOptionFor(flow: VERIFICATION_FLOW) {
    return this.FLOW_OPTIONS.find((opt) => opt.value === flow) ?? null;
  }

  eventById(events: EventView[], id: string | number): EventView | null {
    return events.find((ev) => ev.id === id) ?? null;
  }

  loadList(): void {
    this.loadingList = true;
    this.verifications = [];
    this.verificationItems = [];
    this.currentIndex = 0;
    this.currentDetail = null;
    this.detailView = null;

    this.service
      .getIdentityVerificationList(
        this.client.civilId,
        this.client.brand.code as BrandCodes
      )
      .pipe(
        takeUntil(this.unsubscribe),
        finalize(() => (this.loadingList = false))
      )
      .subscribe({
        next: (data: IdentityVerification[]) => {
          this.verifications = data;
          this.verificationItems = data.map((v) => this.buildListItem(v));
          if (data.length && !this.currentDetail) {
            this.loadDetail(data[0].id);
          } else {
            this.service.identityVerificationDetail$.next(null);
          }
        },
        error: (err: HttpErrorResponse) => {
          console.error(err);
          this.notificationService.showLocalizedErrorMessage({
            notificationText: 'verification.id.loadListError',
          });
        },
      });
  }

  loadDetail(id: number): void {
    this.loadingDetail = true;

    this.service
      .getIdentityVerificationReport(id)
      .pipe(
        takeUntil(this.unsubscribe),
        finalize(() => (this.loadingDetail = false))
      )
      .subscribe({
        next: (data: IdentityVerification) => {
          if (this.currentDetail) {
            this.idVerificationSocket.leaveVerificationRoom(
              this.currentDetail.id
            );
          }
          this.currentDetail = data;
          this.prefetchVideo();
          this.detailView = this.buildDetailView(data);
          this.idVerificationSocket.joinVerificationRoom(this.currentDetail.id);
          this.service.identityVerificationDetail$.next(data);
        },
        error: (err: HttpErrorResponse) => {
          console.error(err);
          this.notificationService.showLocalizedErrorMessage({
            notificationText: 'verification.id.loadDetailError',
          });
        },
      });
  }

  toggleExpanded(): void {
    this.expanded = !this.expanded;
  }

  toggleEvent(id: string | number): void {
    this.selectedEventId = this.selectedEventId === id ? null : id;
  }

  private prefetchVideo(): void {
    this.videoReady = null;

    this.videoBlob$ = this.service.getVerificationVideoUrl$(this.currentDetail.id)
      .pipe(
        switchMap(({ url, accessToken }) =>
          this.shuftiRequestService.getVerificationVideo$(url, accessToken),
        ),
        shareReplay({ bufferSize: 1, refCount: false }),
      );

    // kick off the request now
    this.videoBlob$.pipe(
      takeUntil(this.unsubscribe),
      catchError(() => {
        this.videoReady = false;
        return EMPTY
      }),
    ).subscribe({
      next: () => (this.videoReady = true),
      error: () => { this.videoReady = false },
    });
  }

  openVideoModal(event): void {
    event.stopPropagation();

    this.videoBlob$
      ?.pipe(takeUntil(this.unsubscribe))
      .subscribe((blob) => {
        const videoUrl = URL.createObjectURL(blob);
        this.dialog
          .open(VerificationVideoModalComponent, {
            data: { videoUrl },
            width: '720px',
            panelClass: 'video-dialog',
          })
          .afterClosed()
          .subscribe(() => URL.revokeObjectURL(videoUrl));
      });
  }

  startVerification(): void {
    const latestVerification = this.verifications[0] ?? null;

    const activeVerification =
      latestVerification &&
        (latestVerification.state === ID_VERIFICATION_STATE.IN_PROGRESS ||
          latestVerification.state === ID_VERIFICATION_STATE.IN_REVIEW ||
          latestVerification.state === ID_VERIFICATION_STATE.INITIALIZED)
        ? latestVerification
        : null;

    const data: StartVerificationDialogData = {
      client: this.client,
      previousVerification: latestVerification,
      activeVerification,
    };

    this.verificationStartedFromHere = true;

    this.dialog
      .open<
        StartVerificationDialogComponent,
        StartVerificationDialogData,
        IdentityVerification | null
      >(StartVerificationDialogComponent, {
        data,
        minWidth: '480px',
        maxWidth: '600px',
        autoFocus: false,
        disableClose: true,
      })
      .afterClosed()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((verification) => {
        this.verificationStartedFromHere = false;
        if (!verification) return;
        this.loadList();
      });
  }

  submitDecision(action: VerificationDecision): void {
    const verification = this.verifications[this.currentIndex];
    if (!verification) return;

    const translatedAction = this.translate.instant(
      'verification.id.state.' + action.toUpperCase()
    );

    const config = {
      message: this.translate.instant(
        'verification.id.decisionConfirmMessage',
        {
          action: translatedAction,
        }
      ),
      disableClose: false,
      viewContainerRef: this.viewContainerRef,
      title: this.translate.instant('verification.id.decisionConfirmTitle', {
        action: translatedAction,
      }),
      acceptButton: this.translate.instant('global.confirm'),
      cancelButton: this.translate.instant('global.cancel'),
    };

    this.dialogProvider
      .openConfirm(config)
      .afterClosed()
      .pipe(
        filter((accept) => accept === true),
        mergeMap(() =>
          this.service.changeVerificationStatus$(verification.id, action)
        ),
        takeUntil(this.unsubscribe)
      )
      .subscribe({
        next: () => {
          this.notificationService.showLocalizedSuccessMessage({
            notificationText:
              action === VerificationDecision.SUCCESS
                ? 'verification.id.validateSuccess'
                : 'verification.id.invalidateSuccess',
          });
        },
        error: (err: HttpErrorResponse) => {
          console.error(err);
          this.notificationService.showLocalizedErrorMessage({
            notificationText:
              action === VerificationDecision.SUCCESS
                ? 'verification.id.validateError'
                : 'verification.id.invalidateError',
          });
        },
      });
  }

  selectVerification(index: number): void {
    if (index === this.currentIndex) return;
    this.currentIndex = index;
    this.loadDetail(this.verifications[index].id);
  }

  buildListItem(v: IdentityVerification): VerificationListItem {
    return {
      id: v.id,
      state: v.state,
      startedAt: v.startedAt,
      stateClass: this.STATE_CLASS[v.state] ?? 'expired',
      flow: this.flowOptionFor(v.flow)
    };
  }

  buildDetailView(v: IdentityVerification): VerificationDetailView {
    return {
      id: v.id,
      state: v.state,
      stateClass: this.STATE_CLASS[v.state] ?? 'expired',
      flow: v.flow,
      startedAt: v.startedAt,
      endedAt: v.endedAt,
      duration: this.computeDuration(v.startedAt, v.endedAt),
      miReportId: v.miReportId,
      events: (v.events ?? []).map((ev) => this.buildEventView(ev)),
    };
  }

  getEventTranslationKey(event: IdentityVerificationEvent) {
    if ([
      eventType.VERIFICATION_INITIALIZED,
      eventType.SHUFTI_ONSITE_INITIALIZED,
      eventType.SHUFTI_ONSITE_ENDED,
      eventType.SHUFTI_ONSITE_PARSED,
      eventType.VERIFICATION_IN_PROGRESS,
      eventType.VERIFICATION_IN_REVIEW,
      eventType.VERIFICATION_FAIL,
      eventType.VERIFICATION_SUCCESS,
      eventType.MI_REPORT_PROVISIONED,
      eventType.THEIA_CROSS_VALIDATION_ERROR,
    ].includes(event.type)
    ) {
      return event.type;
    }

    if (event.type === eventType.SHUFTI_ONSITE_CALLBACK) {
      return event.type;
    }

    if ([
      eventType.REJECTED,
      eventType.PROVIDED,
      eventType.VALIDATED,
      eventType.ACCEPTED,
      eventType.CROSS_VALIDATION,
      eventType.SIMPLE_VALIDATION,
      eventType.DOCUMENT_PROVISIONED,
    ].includes(event.type)) {
      if (event.data?.documentType) {
        return `${event.type}_${event.data?.documentType}`;
      }
    }

    return event.type;
  }


  buildEventView(ev: IdentityVerificationEvent): EventView {
    const isFail = /FAIL|REJECT|ERROR/.test(ev.type);
    const isSuccess = /SUCCESS|APPROVE|COMPLETE|MATCH/.test(ev.type);
    const procColor = this.PROC_COLOR[ev.processor] ?? '#546e7a';
    const dataEntries = this.parseEventDataEntries(ev);

    return {
      id: ev.id,
      type: ev.type,
      typeDisplay: this.getEventTranslationKey(ev),
      processor: ev.processor,
      createdAt: ev.createdAt,
      data: ev.data,
      dotColor: isFail ? '#e53935' : isSuccess ? '#43a047' : procColor,
      isFail,
      isSuccess,
      procColor,
      procColorBg: procColor + '14',
      dataEntries,
      hasData: dataEntries.length > 0,
    };
  }

  computeDuration(startedAt: string, endedAt: string | null): string {
    if (!endedAt) return 'ongoing';
    const totalSeconds = Math.round(
      moment(endedAt).diff(moment(startedAt), 'seconds', true)
    );
    if (totalSeconds < 60) return `${totalSeconds}s`;
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}m ${seconds}s`;
  }

  parseEventDataEntries(ev: IdentityVerificationEvent): [string, unknown][] {
    if (!ev?.data) return [];

    let data: any = ev.data;
    if (typeof data === 'string') {
      try {
        data = JSON.parse(data);
      } catch {
        return [];
      }
    }
    if (typeof data !== 'object') return [];

    return Object.entries(data).map(([k, v]) => {
      let display: unknown;
      if (v === null || v === undefined) display = '—';
      else if (typeof v === 'object') display = JSON.stringify(v, null, 2);
      else display = v;
      return [k, display] as [string, unknown];
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
    if (this.currentDetail) {
      this.idVerificationSocket.leaveVerificationRoom(this.currentDetail.id);
    }
    if (this.client?.civilId) {
      this.idVerificationSocket.leaveClientRoom(
        this.client.civilId,
        this.client.brand.code
      );
    }
    this.service.identityVerificationDetail$.next(undefined);
  }
}
